import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import Header from '@/components/Header'
import { useSeo } from '@/components/usePageTitle'
import { lookup } from '@/lib/api'
import { hexContrast } from '@/lib/color'
import { MERCH, fillMerch, swatchSvg, type MerchProduct } from '@/lib/merch'
import { certificateFontCss, canvasToPng, downloadBlob, renderSvg } from '@/lib/certificateExport'
import { canvasToPdf } from '@/lib/pdf'
import type { RegistryColor } from '@/lib/registry'

type Format = 'svg' | 'png' | 'pdf'

/**
 * /color/<slug>/merch: the merchandise designs rendered in the color, each
 * downloadable as SVG, print-size PNG, or PDF. Everything renders in the
 * browser from the templates in public/merch/.
 */
export default function Merch() {
  const { slug = '' } = useParams<{ slug: string }>()
  const [state, setState] = useState<{ slug: string; color: RegistryColor | null } | null>(null)
  useEffect(() => {
    let alive = true
    lookup(slug).then((c) => {
      if (alive) setState({ slug, color: c })
    })
    return () => {
      alive = false
    }
  }, [slug])
  const color = state?.slug === slug ? state.color : undefined

  const templates = useTemplates()

  useSeo({
    title: color ? `${color.name} merchandise` : 'Merchandise',
    description: color ? `Posters, canvases, and artwork in ${color.name} (${color.hex}), ready to print.` : undefined,
    path: color ? `/color/${color.slug}/merch` : undefined,
    image: color ? `/api/swatch?hex=${color.hex.slice(1)}` : undefined,
    themeColor: color?.hex,
  })

  if (color === undefined) return <div className="min-h-svh bg-white" aria-busy="true" />
  if (color === null) {
    return (
      <div className="flex min-h-svh flex-col bg-black text-white">
        <Header tone="light" />
        <main className="flex flex-1 flex-col items-center justify-center px-6 pt-36 text-center">
          <h1 className="font-light">Color not found :(</h1>
          <p className="mt-6">
            <Link to="/register" className="text-white">
              Register it
            </Link>{' '}
            and the merchandise is yours.
          </p>
        </main>
      </div>
    )
  }

  const fg = hexContrast(color.hex)
  return (
    <div className="flex min-h-svh flex-col font-body" style={{ backgroundColor: color.hex, color: fg }}>
      <Header tone={fg === 'white' ? 'light' : 'dark'} />
      <main className="flex-1 px-[36px] pb-12 pt-36 md:pt-44">
        <h1 className="font-display">
          {color.name}
          <sup>CM</sup> merchandise
        </h1>
        <p className="mt-4 max-w-3xl">
          Your color on the ColorMark designs, ready to print. Download any design as SVG, a print-size PNG, or a
          PDF, then order it from the printer of your choice. The{' '}
          <Link to="/store" style={{ color: fg }}>
            store
          </Link>{' '}
          has the finished products.
        </p>
        <ul className="mt-12 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {MERCH.map((p) => (
            <MerchCard key={p.id} product={p} color={color} template={p.template ? templates[p.template] : null} fg={fg} />
          ))}
        </ul>
      </main>
      <footer className="flex flex-wrap items-center justify-between gap-3 px-[36px] pb-[27px] text-sm font-extralight">
        <Link to={`/color/${color.slug}`} className="no-underline hover:underline" style={{ color: fg }}>
          ← Back to {color.name}
        </Link>
        <Link to={`/color/${color.slug}/certificate`} className="no-underline hover:underline" style={{ color: fg }}>
          Certificate of Registration
        </Link>
      </footer>
    </div>
  )
}

/** Fetches every template once; keyed by URL. */
function useTemplates(): Record<string, string> {
  const [templates, setTemplates] = useState<Record<string, string>>({})
  useEffect(() => {
    let alive = true
    const urls = MERCH.map((m) => m.template).filter((t): t is string => t !== null)
    Promise.all(urls.map((u) => fetch(u).then((r) => (r.ok ? r.text() : '')))).then((texts) => {
      if (!alive) return
      const next: Record<string, string> = {}
      urls.forEach((u, i) => {
        if (texts[i]) next[u] = texts[i]
      })
      setTemplates(next)
    })
    return () => {
      alive = false
    }
  }, [])
  return templates
}

function MerchCard({ product, color, template, fg }: { product: MerchProduct; color: RegistryColor; template: string | null; fg: 'black' | 'white' }) {
  const [busy, setBusy] = useState<Format | null>(null)
  const [error, setError] = useState<string | null>(null)
  const preview = useMemo(() => {
    if (product.template) return template ? fillMerch(template, color) : null
    return swatchSvg(color, product.width, product.height)
  }, [product, template, color])

  async function portableSvg(): Promise<string> {
    const fontCss = await certificateFontCss()
    if (product.template) return template ? fillMerch(template, color) : ''
    return swatchSvg(color, product.width, product.height, { fontCss })
  }

  async function download(format: Format) {
    setBusy(format)
    setError(null)
    try {
      const svg = await portableSvg()
      if (!svg) throw new Error('template missing')
      const name = `colormark-${color.slug}-${product.id}`
      if (format === 'svg') {
        downloadBlob(new Blob([svg], { type: 'image/svg+xml' }), `${name}.svg`)
        return
      }
      const scale = product.printWidth / product.width
      const canvas = await renderSvg(svg, { width: product.width * scale, height: product.height * scale }, color.hex)
      if (format === 'png') downloadBlob(await canvasToPng(canvas), `${name}.png`)
      else downloadBlob(await canvasToPdf(canvas, { dpi: product.dpi, title: `${color.name} ${product.title}` }), `${name}.pdf`)
    } catch {
      setError('Sorry, that download failed. Please try again.')
    } finally {
      setBusy(null)
    }
  }

  const btn = fg === 'white' ? 'border border-white/70 text-white hover:bg-white hover:text-black' : 'border border-black/60 text-black hover:bg-black hover:text-white'
  const inches = `${Math.round((product.printWidth / product.dpi) * 10) / 10} × ${Math.round(((product.printWidth / product.width) * product.height) / product.dpi * 10) / 10} in`

  return (
    <li className="flex flex-col">
      <div
        className="flex aspect-square items-center justify-center overflow-hidden rounded-sm bg-white p-6 shadow-[0_8px_30px_rgba(0,0,0,0.18)] [&>svg]:max-h-full [&>svg]:max-w-full [&>svg]:h-auto [&>svg]:w-auto [&>svg]:shadow-[0_2px_12px_rgba(0,0,0,0.25)]"
        // Template artwork with the color substituted; the name is escaped in fillMerch/swatchSvg.
        dangerouslySetInnerHTML={preview ? { __html: preview } : undefined}
        aria-label={`${product.title} in ${color.name}`}
        role="img"
      />
      <h3 className="mt-5 font-display text-2xl">{product.title}</h3>
      <p className="mt-1 text-sm opacity-80">
        {product.blurb} · prints at {inches}, {product.dpi} dpi
      </p>
      <div className="mt-4 flex flex-wrap gap-2 text-sm">
        {(['png', 'pdf', 'svg'] as Format[]).map((f) => (
          <button key={f} type="button" onClick={() => download(f)} disabled={busy !== null || !preview} className={`rounded px-4 py-2 font-medium transition-colors disabled:opacity-50 ${btn}`}>
            {busy === f ? 'Rendering…' : f.toUpperCase()}
          </button>
        ))}
      </div>
      <p className="mt-2 min-h-5 text-sm" role="alert">
        {error}
      </p>
    </li>
  )
}
